import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, ChevronRight } from 'lucide-react';

import ToolCallWrapper from './ToolCall';
import { finishThinking, startThinking } from './utils/thinkingDuration';

function Thinking({
  toolCalls,
  isComplete,
  messageId,
  hasTextResponse,
}: {
  toolCalls: any[];
  isComplete: boolean;
  messageId: string;
  hasTextResponse?: boolean;
}) {
  const { t } = useTranslation('chat');
  const [expanded, setExpanded] = useState(false);
  const autoExpandedRef = useRef(false);

  // This header mounts when the turn starts, before any tool has been called, so the decision to
  // open has to react to the first tool arriving rather than be taken once at mount. It fires at
  // most once per turn, so a reader who collapses the panel keeps it collapsed, and a turn that
  // already has its answer (anything loaded from history) never pops open behind the text.
  //
  // Deliberately NOT conditioned on reasoning parts. That guard dates from when reasoning was
  // rendered inline, and the model emits reasoning before its first tool call — so on the live
  // turns this panel exists for, it was always true by the time the tools landed and the panel
  // never opened at all.
  useEffect(() => {
    if (autoExpandedRef.current || toolCalls.length === 0 || hasTextResponse) {
      return;
    }

    autoExpandedRef.current = true;
    setExpanded(true);
  }, [toolCalls.length, hasTextResponse]);
  // Measured, not assumed: the caller cannot know when thinking began. The lazy initial value is
  // what makes this survive the StreamingMessage → StableMessageList handoff, which remounts this
  // component already complete — the clock it reads was started by the instance before it.
  const [duration, setDuration] = useState(() => (isComplete ? finishThinking(messageId) : 0));

  useEffect(() => {
    if (!isComplete) {
      startThinking(messageId);
      return;
    }

    setDuration(finishThinking(messageId));
  }, [isComplete, messageId]);

  return (
    <div className="my-4">
      <div className="flex items-center cursor-pointer select-none" onClick={() => setExpanded((v) => !v)}>
        <div className="text-xs text-gray-400 font-light flex items-center gap-1">
          {isComplete ? (
            <>
              <CheckCircle className="w-3 h-3 text-gray-400" />
              {/* A message loaded from history was never observed streaming, so there is no
                  measurement to show — floor it at 1 rather than claim it thought for 0. */}
              {t('thought-for-seconds', { duration: duration || 1 })}
            </>
          ) : (
            <>
              <div className="w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin" />
              {t('thinking')}
            </>
          )}
        </div>
        <ChevronRight
          className={`ml-2 w-3 h-3 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-90' : 'rotate-0'}`}
        />
      </div>
      {expanded && (
        <div className="mt-3 space-y-2">
          {toolCalls.map((toolCall, index) => (
            <div key={index}>
              <ToolCallWrapper toolCall={toolCall} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Thinking;
